/** Canvas Global OS File Drop — dispatch contract (VistoeraTASK-00134
 * "实现本地单图与多图直接拖入 Canvas 并自动创建图片素材节点的全局能力").
 *
 * Pure, framework-agnostic helpers only — `CanvasBoard.tsx` owns every
 * side effect (upload, node creation, autosave). Deliberately separate
 * from `canvasDragContract.ts` (that module is the ALLOWLIST for
 * first-party Vistoera in-Canvas HTML5 DnD payloads — palette/creative/
 * script/asset-mention — and must stay scoped to those); this module is
 * the dispatch point for a REAL operating-system file-manager drag,
 * which is a completely different `DataTransfer` shape (`types` contains
 * `'Files'`, payload lives in `.files`/`.items`, never one of Vistoera's
 * own custom MIME strings).
 *
 * Extension point (spec §15) — today only images are handled. A future
 * file kind (video/audio/PDF) should add its own
 * `isAccepted<Kind>File`/classify pair here and a new branch in
 * `CanvasBoard`'s drop dispatch, never grow a single monolithic
 * "figure out what this file is" function. */

/** VistoeraTASK-00220 §四/§十 — the extension point this module's own
 * doc comment anticipated: Canvas OS File Drop now also classifies
 * AUDIO and VIDEO, reusing the exact SAME allow-list modules the
 * canonical upload pickers already use (`audioUploadAllowlist.ts` /
 * `videoUploadAllowlist.ts`), never a second/duplicated MIME list. */
import { isAllowedAudioAssetUploadFile } from '../../../audioUploadAllowlist'
import { isAllowedVideoAssetUploadFile } from '../../../videoUploadAllowlist'

/** Mirrors the current canonical upload pipeline's real image support
 * (`asset_runtime_service.create_asset` opens whatever PIL can decode;
 * the backend is always the final authority — this allowlist is UX-only,
 * so a drop with a "wrong" MIME still reaches the server and gets the
 * server's own real error instead of being silently dropped). */
export const ACCEPTED_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'] as const

const ACCEPTED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.gif']

/** MIME first, filename extension as a fallback — some OS file managers
 * / browsers (notably some Windows Explorer + Chromium combinations for
 * less common extensions, or drags originating from a network share)
 * leave `File.type` empty. Never extension-ONLY (spec §2 "不要只依赖文件
 * 扩展名"). */
export function isAcceptedImageFile(file: Pick<File, 'type' | 'name'>): boolean {
  const type = (file.type || '').toLowerCase()
  if (type) {
    return (ACCEPTED_IMAGE_MIME_TYPES as readonly string[]).includes(type)
  }
  const name = (file.name || '').toLowerCase()
  return ACCEPTED_IMAGE_EXTENSIONS.some((ext) => name.endsWith(ext))
}

export interface ClassifiedDroppedFiles {
  images: File[]
  unsupported: File[]
}

/** Dispatch point for future file kinds (spec §15) — image is the only
 * handled kind today; everything else is `unsupported` (never silently
 * merged into `images`, never thrown — the caller decides the toast/
 * partial-success behavior, spec §7). */
export function classifyDroppedFiles(files: File[]): ClassifiedDroppedFiles {
  const images: File[] = []
  const unsupported: File[] = []
  for (const file of files) {
    if (isAcceptedImageFile(file)) images.push(file)
    else unsupported.push(file)
  }
  return { images, unsupported }
}

export type CanvasMediaKind = 'image' | 'audio' | 'video'

/** VistoeraTASK-00220 — the extension this file's original doc comment
 * called for: a future file kind gets its own `isAccepted<Kind>File`
 * (delegated to the SAME canonical allow-list module every upload UI
 * for that kind already uses) plus one more bucket here. `File.type`
 * (MIME) first, filename extension only as a fallback — never
 * extension-only (spec §十). */
export function isAcceptedAudioFile(file: Pick<File, 'type' | 'name'>): boolean {
  return isAllowedAudioAssetUploadFile(file as File)
}

export function isAcceptedVideoFile(file: Pick<File, 'type' | 'name'>): boolean {
  return isAllowedVideoAssetUploadFile(file)
}

export interface ClassifiedDroppedFilesByMediaType {
  images: File[]
  audios: File[]
  videos: File[]
  unsupported: File[]
}

/** Unified media-type classification for the OS File Drop pipeline
 * (spec §四/§十): every dropped file lands in exactly one bucket. A
 * file that happens to satisfy more than one allow-list (should not
 * happen with the current real MIME sets, which are disjoint) is
 * classified image > audio > video, first-match-wins, so behavior stays
 * fully deterministic even if a future allow-list edit overlaps. */
export function classifyDroppedFilesByMediaType(files: File[]): ClassifiedDroppedFilesByMediaType {
  const images: File[] = []
  const audios: File[] = []
  const videos: File[] = []
  const unsupported: File[] = []
  for (const file of files) {
    if (isAcceptedImageFile(file)) images.push(file)
    else if (isAcceptedAudioFile(file)) audios.push(file)
    else if (isAcceptedVideoFile(file)) videos.push(file)
    else unsupported.push(file)
  }
  return { images, audios, videos, unsupported }
}

/** True while a real OS/file-manager drag (not a Canvas-internal DnD
 * payload) is over the surface. `DataTransfer.types` always contains
 * the literal string `'Files'` for a native file drag in every major
 * browser (Chromium/Firefox/Safari) — well before `dataTransfer.files`
 * is populated, which only ever happens at `drop`. This is what lets
 * `dragenter`/`dragover` distinguish an OS file drag from every other
 * drag kind (palette node-type / creative-context / script-context /
 * asset-mention / plain browser text) using ONLY `dataTransfer.types`,
 * exactly like the existing `isSupportedCanvasPaneDragOver` does for its
 * own allowlist. */
export function isOsFileDrag(dt: DataTransfer | null | undefined): boolean {
  if (!dt) return false
  try {
    return Array.from(dt.types || []).includes('Files')
  } catch {
    return false
  }
}

/** Best-effort "does at least one dragged item look like an image" check
 * for `dragenter`/`dragover` visual feedback ONLY (spec §2 "释放以添加图片
 * 到画布" hint vs. a reject-looking hint) — `dataTransfer.items[].type` IS
 * populated during dragover in every major browser (unlike `.files`,
 * which is drop-only), so the overlay can already show the right
 * message before drop happens. The REAL gate is always
 * `classifyDroppedFiles` at drop time; this only degrades gracefully
 * (returns `true`, i.e. "looks acceptable") when a browser doesn't
 * expose per-item type info during drag at all. */
export function dragHasLikelyImageFile(dt: DataTransfer | null | undefined): boolean {
  if (!dt) return false
  try {
    const items = Array.from(dt.items || [])
    if (items.length === 0) return true
    return items.some((it) => it.kind === 'file' && (!it.type || it.type.startsWith('image/')))
  } catch {
    return true
  }
}

/** VistoeraTASK-00220 — same dragover-only hint check as
 * `dragHasLikelyImageFile` above, broadened to accept image/audio/video
 * (the three kinds `classifyDroppedFilesByMediaType` now handles at
 * drop time) so a valid audio/video OS drag no longer shows the
 * "unsupported" reject hint while hovering. Still best-effort/UX-only —
 * `classifyDroppedFilesByMediaType` at actual drop time is the real
 * gate, same posture as the image-only original. */
export function dragHasLikelyAcceptedMediaFile(dt: DataTransfer | null | undefined): boolean {
  if (!dt) return false
  try {
    const items = Array.from(dt.items || [])
    if (items.length === 0) return true
    return items.some(
      (it) =>
        it.kind === 'file' &&
        (!it.type || it.type.startsWith('image/') || it.type.startsWith('audio/') || it.type.startsWith('video/')),
    )
  } catch {
    return true
  }
}

/** Grid column count for the multi-drop auto-layout (spec §6) — a small
 * reusable helper, NOT a hardcoded product rule. 1 file sits exactly on
 * the drop point (`placeImageAssetGrid` already centers a single item
 * on its anchor with zero offset), 2-4 lay out in a single row, 5+ fall
 * back to a roughly-square grid sized to the batch (~sqrt(N), rounded,
 * never fewer than 2 columns once we're in "grid" territory). Actual
 * cell placement always goes through the existing
 * `placeImageAssetGrid` primitive (`imageAssetPickerContract.ts`) — this
 * function only picks how many columns to hand it. */
export function dropGridColumns(count: number): number {
  if (count <= 1) return 1
  if (count <= 4) return count
  return Math.max(2, Math.round(Math.sqrt(count)))
}
